import { toast as sonnerToast } from 'sonner';

/**
 * Thin wrapper around sonner so the rest of the app doesn't import sonner
 * directly. Two reasons for the wrapper:
 *
 *   1. Single place to normalize error payloads (axios errors usually carry
 *      the user-facing message at err.response.data.error).
 *   2. Branded defaults — duration, position, and icon size live here so
 *      swapping the library later is one file.
 *
 * Usage:
 *   import { toast } from '../lib/toast';
 *
 *   toast.success('Schema saved');
 *   toast.error(err, 'Could not save schema');
 *   toast.info('Tracking script copied to clipboard');
 *   toast.promise(api.post(...), {
 *     loading: 'Generating report…',
 *     success: 'Report ready',
 *     error: 'Could not generate report',
 *   });
 */

// Extract a user-facing message from anything — axios error, Error, string.
export function messageFromError(err, fallback = 'Something went wrong') {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  // Axios-style errors
  const apiMsg = err?.response?.data?.error;
  if (apiMsg) return apiMsg;
  if (err.message) return err.message;
  return fallback;
}

export const toast = {
  success(message, opts = {}) {
    return sonnerToast.success(message, { duration: 3500, ...opts });
  },

  // Accepts either a string or an Error/axios-error as the first arg.
  // When given an error, the second arg is the fallback message.
  error(err, fallback = 'Something went wrong', opts = {}) {
    const message = typeof err === 'string' ? err : messageFromError(err, fallback);
    return sonnerToast.error(message, { duration: 5000, ...opts });
  },

  info(message, opts = {}) {
    return sonnerToast(message, { duration: 3500, ...opts });
  },

  warning(message, opts = {}) {
    return sonnerToast.warning(message, { duration: 4000, ...opts });
  },

  // Wraps a promise with loading → success/error states automatically.
  promise(promise, { loading, success, error = 'Something went wrong', ...opts } = {}) {
    return sonnerToast.promise(promise, {
      loading,
      success,
      error: (err) => messageFromError(err, error),
      ...opts,
    });
  },

  // Manual dismiss
  dismiss(id) {
    return sonnerToast.dismiss(id);
  },
};

export default toast;
